import {useTranslation} from '@aboutyou/i18n';
import React, {useRef} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {SIZE} from '../../../constants/sizing';
import {ExpandButton} from './ExpandButton';
import {useExpanded} from './utils/useExpanded';

interface Props {
  dataTestId?: string;
  alwaysRenderedContent?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
  isInitiallyExpanded?: boolean;
}

export const FilterBar = ({
  dataTestId = 'FilterBar',
  className,
  children,
  alwaysRenderedContent,
  isInitiallyExpanded = false,
  ref,
}: Props & {ref?: React.Ref<HTMLDivElement>}) => {
  const {t} = useTranslation();

  const controlRef = useRef<HTMLElement | null>(null);
  const optionalControlRef = useRef<HTMLButtonElement | null>(null);
  const itemListRef = useRef<HTMLDivElement | null>(null);
  const {expanded, shouldDisplayExpandedButton, toggleExpanded} = useExpanded(
    {
      control: controlRef,
      itemList: itemListRef,
      optionalControl: optionalControlRef,
      wrapper: ref as React.MutableRefObject<HTMLElement | null>,
    },
    isInitiallyExpanded,
  );

  return (
    <Wrapper
      ref={ref}
      className={className}
      data-testid={dataTestId}
      role="region"
      aria-label={t('Filters')}
    >
      <ItemList
        $inline={expanded}
        data-testid={`${dataTestId}_${expanded ? 'expanded' : 'collapsed'}`}
        ref={itemListRef}
      >
        {children}
        <Controls
          $inline={expanded}
          $canExpand={shouldDisplayExpandedButton}
          ref={controlRef}
        >
          {shouldDisplayExpandedButton && (
            <ExpandButton
              ref={optionalControlRef}
              expanded={expanded}
              onClick={toggleExpanded}
              tabIndex={-1}
            />
          )}
          {alwaysRenderedContent}
        </Controls>
      </ItemList>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  position: relative;
  // similar to overflow: hidden, but it allows to absolute position elements that move out of the elements box on the bottom of the element
  // -3px in the top is for the border on the controls
  clip-path: inset(-3px 0 -100vh 0);
  z-index: 1;
`;

const ItemList = styled.div<{$inline: boolean}>`
  display: inline-flex;
  align-items: start;
  flex-wrap: ${({$inline}) => ($inline ? 'wrap' : 'nowrap')};
  gap: ${SIZE.Xs};
  > * {
    flex-shrink: 0;
  }
`;

const Controls = styled.aside<{$inline: boolean; $canExpand: boolean}>`
  display: inline-flex;
  flex-wrap: no-wrap;
  background: ${COLORS.White};
  right: 0;
  top: -3px;
  bottom: -3px;
  z-index: 3;
  box-shadow: ${({$inline, $canExpand}) =>
    $inline || !$canExpand ? 'none' : `-20px 0px 10px 10px ${COLORS.White}`};
  border-left: 1px solid
    ${({$inline}) => ($inline ? 'transparent' : COLORS.Grey3)};
  position: ${({$inline}) => ($inline ? 'static' : 'absolute')};
  padding: ${({$inline}) => ($inline ? '0' : '0 16px')};
  &:empty {
    display: none;
  }
`;
